import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconPlusOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";

/**
 * FidSearchInput — Farcaster FID input with username search.
 *
 * Supports two modes:
 * - multi=true (default): Array of FIDs with tags, for trigger filter fields
 * - multi=false: Single FID number, for action input fields (e.g. get-farcaster-feed fid)
 *
 * Users type a Farcaster username, the component searches via the backend
 * get-farcaster-user action and shows matching users with avatar/display name.
 */
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useCallback, useEffect, useRef, useState } from "react";
interface FarcasterUserResult {
  fid: number;
  username: string;
  displayName: string | null;
  pfpUrl: string | null;
}
interface FidSearchMultiProps {
  label: string;
  description?: string;
  multi: true;
  value: number[];
  onChange: (value: number[]) => void;
}
interface FidSearchSingleProps {
  label: string;
  description?: string;
  multi?: false;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}
type FidSearchInputProps = FidSearchMultiProps | FidSearchSingleProps;
export default function FidSearchInput(props: FidSearchInputProps) {
  const {
    label,
    description
  } = props;
  const isMulti = props.multi === true;

  // Normalize to array internally
  const selectedFids: number[] = isMulti ? (props as FidSearchMultiProps).value : (props as FidSearchSingleProps).value ? [(props as FidSearchSingleProps).value as number] : [];
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FarcasterUserResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [manualFid, setManualFid] = useState("");
  const [isManualMode, setIsManualMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState<FarcasterUserResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const searchIdRef = useRef(0);
  const fetchMountedRef = useRef(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      fetchMountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Single mode: restore user metadata for pre-existing FID on mount
  useEffect(() => {
    if (!isMulti && selectedFids.length > 0 && !selectedUser) {
      fetchUserByFid(selectedFids[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchUserByFid = async (fid: number) => {
    try {
      // Uses the action test endpoint — standard path for frontend action execution in the workflow editor
      const res = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/get-farcaster-user/test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          inputs: {
            fid
          }
        })
      });
      if (!res.ok) return;
      const data = await res.json();
      const result = data?.data?.result || data?.result || data?.data;
      if (result?.fid && fetchMountedRef.current) {
        setSelectedUser({
          fid: result.fid,
          username: result.username || "",
          displayName: result.displayName || null,
          pfpUrl: result.pfpUrl || null
        });
      }
    } catch {
      // Silently fail — just show bare FID
    }
  };
  const searchUsers = useCallback(async (q: string) => {
    const minChars = /^\d+$/.test(q.trim()) ? 1 : 2;
    if (!q || q.length < minChars) {
      setResults([]);
      return;
    }

    // Increment search ID to ignore stale responses
    const thisSearchId = ++searchIdRef.current;
    setIsLoading(true);
    try {
      const isNumeric = /^\d+$/.test(q.trim());
      const inputs = isNumeric ? {
        fid: Number(q.trim())
      } : {
        query: q
      };

      // Uses the action test endpoint — standard path for frontend action execution in the workflow editor
      const res = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/get-farcaster-user/test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          inputs
        })
      });

      // Ignore response if a newer search was triggered
      if (thisSearchId !== searchIdRef.current) return;
      if (!res.ok) {
        setResults([]);
        return;
      }
      const data = await res.json();
      const result = data?.data?.result || data?.result || data?.data;

      // Query mode returns { users: [...] }, single lookup returns { fid, username, ... }
      if (result?.users && Array.isArray(result.users)) {
        setResults(result.users.map((u: Record<string, unknown>) => ({
          fid: u.fid as number,
          username: u.username as string || "",
          displayName: u.displayName as string || null,
          pfpUrl: u.pfpUrl as string || null
        })));
      } else if (result?.fid) {
        setResults([{
          fid: result.fid,
          username: result.username || "",
          displayName: result.displayName || null,
          pfpUrl: result.pfpUrl || null
        }]);
      } else {
        setResults([]);
      }
    } catch {
      setResults([]);
    } finally {
      if (thisSearchId === searchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);
  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    setIsOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchUsers(q), 500);
  };
  const emitChange = (fids: number[]) => {
    if (isMulti) {
      (props as FidSearchMultiProps).onChange(fids);
    } else {
      (props as FidSearchSingleProps).onChange(fids[0] ?? undefined);
    }
  };
  const handleSelectUser = (user: FarcasterUserResult) => {
    if (isMulti) {
      if (!selectedFids.includes(user.fid)) {
        emitChange([...selectedFids, user.fid]);
      }
    } else {
      emitChange([user.fid]);
      setSelectedUser(user);
    }
    setQuery("");
    setResults([]);
    setIsOpen(false);
  };
  const handleRemove = (index: number) => {
    const updated = selectedFids.filter((_, i) => i !== index);
    emitChange(updated);
    if (!isMulti) setSelectedUser(null);
  };
  const handleManualAdd = () => {
    const fid = Number(manualFid.trim());
    if (isNaN(fid) || fid <= 0 || !Number.isInteger(fid)) return;
    if (isMulti) {
      if (!selectedFids.includes(fid)) {
        emitChange([...selectedFids, fid]);
      }
    } else {
      emitChange([fid]);
      fetchUserByFid(fid);
    }
    setManualFid("");
  };
  const handleManualKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleManualAdd();
    }
  };

  // Single mode: show selected user inline
  if (!isMulti && selectedFids.length > 0) {
    return <div className="space-y-2" ref={containerRef}>
        {label && <label className="field-label block">{label}</label>}
        {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
        <div className="border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card flex items-center gap-2 rounded-lg border px-3 py-2">
          {selectedUser?.pfpUrl ? <img src={selectedUser.pfpUrl} alt="" className="h-6 w-6 rounded-full object-cover" /> : selectedUser?.username ? <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 text-xs font-medium text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
              {selectedUser.username[0].toUpperCase()}
            </div> : null}
          <span className="text-ds-text-primary flex-1 text-sm">
            {selectedUser?.username ? `@${selectedUser.username} · ` : ""}FID: {selectedFids[0]}
          </span>
          <button type="button" onClick={() => handleRemove(0)} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-700 rounded p-0.5">
            <IconXmarkOutline18 className="text-ds-text-disabled h-4 w-4" />
          </button>
        </div>
      </div>;
  }
  return <div className="space-y-2" ref={containerRef}>
      {label && <label className="field-label block">{label}</label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      {/* Selected FIDs (multi mode only) */}
      {isMulti && selectedFids.length > 0 && <div className="flex flex-wrap gap-2">
          {selectedFids.map((fid, index) => <div key={fid} className="flex items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-sm text-blue-700 dark:bg-blue-600/20 dark:text-blue-400">
              <span>{fid}</span>
              <button type="button" onClick={() => handleRemove(index)} className="rounded hover:bg-blue-200 dark:hover:bg-blue-600/30">
                <IconXmarkOutline18 className="h-3 w-3" />
              </button>
            </div>)}
        </div>}
      {/* Toggle between search and manual */}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setIsManualMode(false)} className={`text-xs transition-colors ${!isManualMode ? "font-medium text-blue-600 dark:text-blue-400" : "text-ds-text-muted hover:text-ds-text-secondary"}`}>
          Search by username
        </button>
        <span className="text-ds-text-disabled text-xs">|</span>
        <button type="button" onClick={() => setIsManualMode(true)} className={`text-xs transition-colors ${isManualMode ? "font-medium text-blue-600 dark:text-blue-400" : "text-ds-text-muted hover:text-ds-text-secondary"}`}>
          Enter FID directly
        </button>
      </div>
      {isManualMode ? <div className="flex gap-2">
          <Input type="text" value={manualFid} onChange={e => setManualFid(e.target.value)} onKeyDown={handleManualKeyDown} placeholder="Enter FID number" className="text-ds-text-primary flex-1" />
          <button type="button" onClick={handleManualAdd} className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700">
            <IconPlusOutline18 className="h-4 w-4" />
          </button>
        </div> : <div className="relative">
          <div className="relative">
            <IconMagnifyingGlass className="text-ds-text-disabled absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input type="text" value={query} onChange={handleQueryChange} onFocus={() => {
          const minChars = /^\d+$/.test(query.trim()) ? 1 : 2;
          if (query.length >= minChars) setIsOpen(true);
        }} placeholder="Search by username or FID..." className="text-ds-text-primary py-2 pl-9 pr-3" />
          </div>

          {(() => {
        const meetsMinChars = /^\d+$/.test(query.trim()) ? query.length >= 1 : query.length >= 2;
        return isOpen && (meetsMinChars || results.length > 0) ? <div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border">
                {isLoading && <div className="text-ds-text-muted px-3 py-2 text-sm">Searching...</div>}
                {!isLoading && results.length === 0 && meetsMinChars && <div className="text-ds-text-muted px-3 py-2 text-sm">No users found</div>}
                {results.map(user => {
            const alreadySelected = selectedFids.includes(user.fid);
            return <button key={user.fid} type="button" disabled={alreadySelected} onClick={() => handleSelectUser(user)} className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${alreadySelected ? "cursor-default opacity-50" : "hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-700"}`}>
                      {user.pfpUrl ? <img src={user.pfpUrl} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-xs font-medium text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                          {(user.username || "?")[0].toUpperCase()}
                        </div>}
                      <div className="flex-1 overflow-hidden">
                        <div className="text-ds-text-primary truncate text-sm font-medium">
                          {user.displayName || user.username}
                        </div>
                        <div className="text-ds-text-muted truncate text-xs">
                          @{user.username} · FID: {user.fid}
                        </div>
                      </div>
                      {alreadySelected && <span className="text-ds-text-disabled text-xs">Added</span>}
                    </button>;
          })}
              </div> : null;
      })()}
        </div>}
    </div>;
}